/**
 * @mhmo91/ican — `can(verb).when(cond) [.writes(pattern) | .requiresHandler(name)]`.
 *
 * Verb-first companion to `when(cond).allow(...verbs)`. Each `can(...)`
 * call produces a single-verb `PendingRule`. Three categories emerge
 * naturally from the chain:
 *
 *   1. CRUD verb — `can('read').when(cond)` returns a plain
 *      PendingRule. Maps 1:1 to a Firestore primitive (`read`,
 *      `create`, `update`, `delete`).
 *
 *   2. Business verb with a Firestore-enforceable shape —
 *      `can('dispatchOrder').when(cond).writes(w => w.update({...}).from(...))`
 *      attaches a `pendingWritePattern`. The Firestore emitter gates
 *      the FE-direct write to exactly that pattern (pre-state +
 *      field mask + post-state values).
 *
 *   3. Business verb that must run on the server —
 *      `can('refundCustomer').when(cond).requiresHandler('refundCustomer')`
 *      attaches a handler-routing marker. The Firestore emitter emits no
 *      allow line for the verb, so the named action is the only legal
 *      entry point.
 *
 * Both categories 1–2 are `PendingRule`s and are returned from
 * `entity.pipe(({can, when, ...}) => [ ... ])` alongside legacy
 * `when().allow()` rules. Both forms coexist in the same pipe block
 * during migration.
 */
import type { z } from 'zod';
import type { UserBase, UserProxy, Cond, DataProxy, Rule, WriteValue } from './types.ts';
import {
  PENDING_BRAND,
  type PendingPreState,
  type PendingRule,
  type PendingWritePattern,
} from './when.ts';

// ---------------------------------------------------------------
// WritePatternFactory — the `w` argument inside .writes(w => ...)
// ---------------------------------------------------------------

/**
 * Update-pattern builder. `from(predicate)` adds a pre-state condition;
 * calling it again replaces the previous predicate (idempotent).
 */
export type UpdatePatternBuilder<Schema extends z.ZodTypeAny> = PendingWritePattern<Schema> & {
  readonly kind: 'update';
  from(predicate: (data: DataProxy<Schema>) => Cond): UpdatePatternBuilder<Schema>;
};

/**
 * Field-mask update builder — same `.from(predicate)` pre-state chain as
 * {@link UpdatePatternBuilder}, with post-state values only where `.values(...)`
 * pins them.
 */
export type UpdateMaskPatternBuilder<Schema extends z.ZodTypeAny> = PendingWritePattern<Schema> & {
  readonly kind: 'updateMask';
  from(predicate: (data: DataProxy<Schema>) => Cond): UpdateMaskPatternBuilder<Schema>;
  /**
   * Pin the post-state value of fields the mask admits, in the tokens
   * `.update` and `.set` take (`'serverTime'`, `'me.uid'`, literals); every
   * other masked field stays free. A field outside the mask throws — it could
   * only ever match by already holding the value.
   */
  values(values: Readonly<Record<string, WriteValue>>): UpdateMaskPatternBuilder<Schema>;
};

export type DeletePatternBuilder<Schema extends z.ZodTypeAny> = PendingWritePattern<Schema> & {
  readonly kind: 'delete';
  from(predicate: (data: DataProxy<Schema>) => Cond): DeletePatternBuilder<Schema>;
};

/**
 * Create-pattern builder. There is no `.from(...)` — a create has no
 * pre-state to predicate on. `.keys(allowed)` requires exactly those keys;
 * `.keys(allowed, required)` admits optional allowed keys while requiring
 * the listed subset. No other key may be present.
 */
export type SetPatternBuilder<Schema extends z.ZodTypeAny> = PendingWritePattern<Schema> & {
  readonly kind: 'set';
  keys(
    fields: ReadonlyArray<keyof z.infer<Schema> & string>,
    requiredFields?: ReadonlyArray<keyof z.infer<Schema> & string>,
  ): SetPatternBuilder<Schema>;
};

export type WritePatternFactory<Schema extends z.ZodTypeAny> = {
  /** Field-mask + new values for an update. Map auto-derives from the keys. */
  update(values: Readonly<Record<string, WriteValue>>): UpdatePatternBuilder<Schema>;
  /**
   * Field mask alone: the listed fields are the ONLY ones a client update
   * may change, and each one's value is unconstrained unless `.values()` pins
   * it. Every other field on
   * the schema becomes server-written — an update touching one is refused.
   *
   * The list is typed against `keyof z.infer<Schema>`, so renaming or
   * dropping a masked field fails `tsc` at this call site. ADDING a field to
   * the schema does not: the new field is silently server-only until it is
   * named here, which is the amendment obligation each consuming entity
   * states beside its own schema and binds with a coverage test.
   */
  updateMask(
    fields: ReadonlyArray<keyof z.infer<Schema> & string>,
  ): UpdateMaskPatternBuilder<Schema>;
  /**
   * Full doc for a create. `values` pins the fields whose value the policy
   * fixes (a server timestamp, the caller's own uid); `.keys(allowed, required?)`
   * closes the document's key set, leaving every unpinned one's value free.
   * Without a required subset, every allowed key must be present.
   *
   * The key list is typed against `keyof z.infer<Schema>`, so renaming or
   * dropping a listed field fails `tsc` at the call site. ADDING a field to
   * the schema does not, which is the amendment obligation each consuming
   * entity states beside its own schema and binds with a fixture.
   */
  set(values: Readonly<Record<string, WriteValue>>): SetPatternBuilder<Schema>;
  /** Optional pre-state predicate via `.from(...)`. */
  delete(): DeletePatternBuilder<Schema>;
};

const buildUpdate = <Schema extends z.ZodTypeAny>(
  values: Readonly<Record<string, WriteValue>>,
  preStateCallback?: PendingPreState<Schema>,
): UpdatePatternBuilder<Schema> => {
  const base: UpdatePatternBuilder<Schema> = {
    kind: 'update',
    values,
    ...(preStateCallback ? { preStateCallback } : {}),
    from(predicate) {
      return buildUpdate<Schema>(values, predicate);
    },
  };
  return base;
};

const buildUpdateMask = <Schema extends z.ZodTypeAny>(
  fields: ReadonlyArray<keyof z.infer<Schema> & string>,
  preStateCallback?: PendingPreState<Schema>,
  pinnedValues?: Readonly<Record<string, WriteValue>>,
): UpdateMaskPatternBuilder<Schema> => ({
  kind: 'updateMask',
  fields,
  ...(preStateCallback ? { preStateCallback } : {}),
  ...(pinnedValues ? { pinnedValues } : {}),
  from(predicate) {
    return buildUpdateMask<Schema>(fields, predicate, pinnedValues);
  },
  values(next) {
    if (pinnedValues !== undefined) {
      throw new Error(
        '@mhmo91/ican: updateMask(...).values(...) is already set — a second call would replace the first pins and loosen the rule; pass every pinned field in one map',
      );
    }
    if (Object.keys(next).length === 0) {
      throw new Error(
        '@mhmo91/ican: updateMask(...).values({}) pins nothing — omit .values(...) for a mask with no pinned field',
      );
    }
    const outside = Object.keys(next).find((key) => !fields.some((field) => field === key));
    if (outside !== undefined) {
      throw new Error(
        `@mhmo91/ican: updateMask(...).values(...) pins '${outside}', which the mask does not admit — a pinned field must be one the update may change`,
      );
    }
    return buildUpdateMask<Schema>(fields, preStateCallback, next);
  },
});

const buildSet = <Schema extends z.ZodTypeAny>(
  values: Readonly<Record<string, WriteValue>>,
  fields?: ReadonlyArray<keyof z.infer<Schema> & string>,
  requiredFields?: ReadonlyArray<keyof z.infer<Schema> & string>,
): SetPatternBuilder<Schema> => ({
  kind: 'set',
  values,
  ...(fields ? { fields } : {}),
  ...(requiredFields ? { requiredFields } : {}),
  keys(next, required) {
    const outside = required?.find((key) => !next.includes(key));
    if (outside !== undefined) {
      throw new Error(
        `@mhmo91/ican: set(...).keys(...) requires '${outside}', which the allowed keys do not admit`,
      );
    }
    return buildSet<Schema>(values, next, required);
  },
});

const buildDelete = <Schema extends z.ZodTypeAny>(
  preStateCallback?: PendingPreState<Schema>,
): DeletePatternBuilder<Schema> => ({
  kind: 'delete',
  ...(preStateCallback ? { preStateCallback } : {}),
  from(predicate) {
    return buildDelete<Schema>(predicate);
  },
});

const makeWritePatternFactory = <Schema extends z.ZodTypeAny>(): WritePatternFactory<Schema> => ({
  update: (values) => buildUpdate<Schema>(values),
  updateMask: (fields) => buildUpdateMask<Schema>(fields),
  set: (values) => buildSet<Schema>(values),
  delete: () => buildDelete<Schema>(),
});

// ---------------------------------------------------------------
// can(verb) — verb-first builder
// ---------------------------------------------------------------

/**
 * Result of `can(verb).when(cond)`. It is itself a `PendingRule`
 * (passes `isPendingRule()`, can be returned from `.pipe(...)` as-is)
 * AND carries `.writes(...)` / `.requiresHandler(...)` chainable
 * methods that produce refined `PendingRule`s with the appropriate
 * marker fields populated.
 */
/**
 * Result of `can(verb).when(cond)`. Typed as `Rule<...>` so it satisfies
 * the `pipe(...)` return contract; at runtime it is actually a
 * `PendingRule` (carries `[PENDING_BRAND]`), which the pipe finalizer
 * recognizes and re-shapes into a real `Rule` with the frozen `cond`.
 *
 * Same type-fudge as `when(cond).allow(...)`.
 */
export type WhenChain<User extends UserBase, Schema extends z.ZodTypeAny, V extends string> = Rule<
  User,
  z.infer<Schema>,
  V
> & {
  /**
   * Attach a write-pattern guard. The Firestore emitter will gate any
   * direct FE write of the rule's verb to exactly the declared shape.
   */
  writes(
    build: (w: WritePatternFactory<Schema>) => PendingWritePattern<Schema>,
  ): Rule<User, z.infer<Schema>, V>;
  /**
   * Mark the rule as server-only: `name` is the ACTION that performs the
   * verb, never the function it is deployed inside. The emitter reads this
   * marker's presence alone — it emits no allow line for the verb, so
   * nothing direct reaches it — and the action lives behind its domain's
   * router, which is the deployed callable.
   *
   * The action is what does not move: a router regroups, re-splits, or is
   * renamed on the server's own schedule, and a `handlerName` tracking that
   * would re-couple every entity in `packages/types` to the deployment
   * topology. Naming the action is also what keeps sibling rules apart —
   * one router answers many verbs, so a deployment-named marker reads the
   * same on all of them. The distinction was invisible while every declared
   * value was simultaneously an action and its own 1:1 callable; it resolves
   * the first time a domain folds its callables into one router.
   *
   * The name is stored on the finalized rule under `handlerName` (the field
   * name differs from the method name to avoid a TypeScript collision on
   * `Rule`).
   */
  requiresHandler(name: string): Rule<User, z.infer<Schema>, V>;
};

/**
 * Build a `can(...)` factory typed against the given actor + schema +
 * actions tuple. The verb argument is constrained to the entity's
 * declared `Actions[number]` so a typo fails at compile time.
 */
export const makeCan = <
  User extends UserBase,
  Schema extends z.ZodTypeAny,
  Actions extends readonly string[],
>() => {
  return <V extends Actions[number]>(verb: V) => ({
    when(
      callback: (data: DataProxy<Schema>, me: UserProxy<User>) => Cond,
    ): WhenChain<User, Schema, V> {
      const base: PendingRule<User, Schema, V> = {
        effect: 'allow',
        actions: [verb] as ReadonlyArray<V>,
        [PENDING_BRAND]: true,
        _callback: callback,
      };
      const chain = {
        ...base,
        writes(build: (w: WritePatternFactory<Schema>) => PendingWritePattern<Schema>) {
          const pending = build(makeWritePatternFactory<Schema>());
          return Object.freeze({
            ...base,
            pendingWritePattern: pending,
          }) as unknown as Rule<User, z.infer<Schema>, V>;
        },
        requiresHandler(name: string) {
          return Object.freeze({
            ...base,
            handlerName: name,
          }) as unknown as Rule<User, z.infer<Schema>, V>;
        },
      };
      return chain as unknown as WhenChain<User, Schema, V>;
    },
  });
};
